import { useEffect, useState } from 'react';
import { FlexContainer, Text, Spinner } from '@taxdown/piccolo-web';
import { locizeNS } from 'Services/i18n';
import { PreviousYearRecord } from 'Services/sat/types';
import useTranslation from 'Hooks/useTranslation';
import { lottiesPaths } from 'Constants/lottiesPaths';
import * as Styled from '../../styles';
import ProgressDisplay from '../ProgressDisplay';
import RecordRotator from '../RecordRotator';
import { useFetchPollingCalculatingPreviousYears, usePreviousYearData } from './hooks';

const baseInterval = 15 * 1000;

const CalculatingLoader = (): JSX.Element => {
  const { t } = useTranslation(locizeNS.PY);
  const [callHook, setCallHook] = useState(false);
  const [records, setRecords] = useState<PreviousYearRecord[]>([]);
  useFetchPollingCalculatingPreviousYears(callHook);
  const previousYearData = usePreviousYearData();
  const [timeLimit, setTimeLimit] = useState(false);
  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCallHook(true);
    }, 15000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (previousYearData) {
      const items = previousYearData.records.filter(item => item.isRecoverable);
      setRecords(items);
      setTotalAmount(Math.abs(previousYearData.amountTotal));
    }
  }, []);

  useEffect(() => {
    let timer;
    if (records.length > 0) {
      const timeout = records.length * baseInterval || records.length * 15 * 1000;
      timer = setTimeout(() => {
        setTimeLimit(true);
      }, timeout);
    }
    return () => {
      clearTimeout(timer);
    };
  }, [records]);

  return (
    <Styled.ThreeColLayout>
      <Styled.TitleContainer type="headline" color="dark" size="S_M_S" testID="desktop-title">
        {t('PreviousYears.title')}
      </Styled.TitleContainer>
      <FlexContainer direction="column">
        <Styled.Title type="headline" color="dark" size="S_M_S" testID="mobile-title">
          {t('PreviousYears.title')}
        </Styled.Title>
        <Styled.ContainerInProgress>
          <Styled.LoaderContainer
            direction="column"
            align="center"
            justify="center"
            gap="dimension_08"
          >
            <Styled.LottieContainer options={{ path: lottiesPaths.FILTER_DATA }} />
            {records.length > 0 ? (
              <>
                <RecordRotator records={records} />
                <ProgressDisplay totalAmount={totalAmount} />
                {timeLimit && (
                  <Text type="paragraphs" size="XS_L_S" color="success_strong" align="center">
                    {t('PreviousYears.calculating.timeLimit')}
                  </Text>
                )}
              </>
            ) : (
              <Spinner size="M" align="center" justify="center" />
            )}
          </Styled.LoaderContainer>
        </Styled.ContainerInProgress>
      </FlexContainer>
    </Styled.ThreeColLayout>
  );
};

export default CalculatingLoader;
